import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import AppShell from '../components/AppShell';
import CategoryManager from '../components/catalog/CategoryManager';

const UNITS = { piece:'шт', liter:'л', gram:'кг' };
const UNIT_OPTIONS = [
  { value:'piece', label:'Штука (шт)' },
  { value:'liter', label:'Літри (л)' },
  { value:'gram', label:'Грами/кг' },
];

const emptyForm = {
  name:'', price:'', unit:'piece', category_id:'', barcode:'',
  is_excise:false, uktzed:'', tax_group:'',
};

export default function AssortmentPage() {
  const { isSuperAdmin } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showCats, setShowCats] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('all');
  const [error, setError] = useState('');

  useEffect(() => { loadData(); }, []);

  async function loadData() {
    setLoading(true);
    const [p, c] = await Promise.all([
      api.get('/catalog/assortment').then(r => r.data).catch(() => []),
      api.get('/catalog/categories').then(r => r.data).catch(() => []),
    ]);
    setProducts(p);
    setCategories(c);
    setLoading(false);
  }

  function startCreate() {
    setForm(emptyForm); setEditingId(null); setShowForm(true); setError('');
  }

  function startEdit(p) {
    setForm({
      name: p.name, price: p.price, unit: p.unit, category_id: p.category_id || '',
      barcode: p.barcode || '', is_excise: p.is_excise,
      uktzed: p.uktzed || '', tax_group: p.tax_group || '',
    });
    setEditingId(p.id); setShowForm(true); setError('');
  }

  async function submitForm(e) {
    e.preventDefault(); setError('');
    const payload = {
      name: form.name, price: form.price, unit: form.unit,
      category_id: form.category_id || null, barcode: form.barcode || null,
      is_excise: form.is_excise, uktzed: form.uktzed || null, tax_group: form.tax_group || null,
    };
    try {
      if (editingId) await api.patch(`/catalog/products-network/${editingId}`, payload);
      else await api.post('/catalog/products-network', payload);
      await loadData();
      setShowForm(false); setForm(emptyForm); setEditingId(null);
    } catch(err) {
      const detail = err.response?.data?.detail;
      setError(Array.isArray(detail) ? detail.map(e => e.msg).join(', ') : (detail || 'Помилка'));
    }
  }

  async function removeProduct(p) {
    if (!window.confirm(`Прибрати "${p.name}" з каталогу всієї мережі?`)) return;
    await api.delete(`/catalog/products-network/${p.id}`);
    await loadData();
  }

  const catName = (id) => categories.find(c => c.id === id)?.name || '—';
  const filtered = products.filter(p =>
    (filterCat === 'all' || p.category_id === filterCat) &&
    p.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <AppShell>
      <div style={styles.head}>
        <div>
          <h1 style={styles.h1}>🍺 Асортимент мережі</h1>
          <p style={styles.sub}>Товари, категорії та фіскальні коди — спільні на всі магазини. Склад і залишки — у кожному магазині окремо.</p>
        </div>
        {isSuperAdmin && (
          <div style={styles.headBtns}>
            <button style={styles.ghostBtn} onClick={() => setShowCats(s => !s)}>📂 Категорії</button>
            <button style={styles.addBtn} onClick={startCreate}>+ Додати товар</button>
          </div>
        )}
      </div>

      {showCats && (
        <div style={styles.card}>
          <CategoryManager network categories={categories} onUpdate={loadData} />
        </div>
      )}

      {showForm && (
        <div style={styles.card}>
          <form onSubmit={submitForm}
            onKeyDown={e => { if (e.key === 'Enter' && e.target.tagName === 'INPUT') e.preventDefault(); }}>
            <div style={styles.row}>
              <div style={styles.fg}>
                <label style={styles.label}>Назва</label>
                <input style={styles.input} value={form.name} required
                  onChange={e => setForm(f => ({...f, name:e.target.value}))} />
              </div>
              <div style={{...styles.fg, maxWidth:'130px'}}>
                <label style={styles.label}>Базова ціна</label>
                <input style={styles.input} type="number" step="0.01" value={form.price} required
                  onChange={e => setForm(f => ({...f, price:e.target.value}))} />
              </div>
              <div style={{...styles.fg, maxWidth:'150px'}}>
                <label style={styles.label}>Одиниця</label>
                <select style={styles.input} value={form.unit}
                  onChange={e => setForm(f => ({...f, unit:e.target.value}))}>
                  {UNIT_OPTIONS.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
                </select>
              </div>
            </div>
            <div style={styles.row}>
              <div style={styles.fg}>
                <label style={styles.label}>Категорія</label>
                <select style={styles.input} value={form.category_id}
                  onChange={e => setForm(f => ({...f, category_id:e.target.value}))}>
                  <option value="">— без категорії —</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div style={styles.fg}>
                <label style={styles.label}>Штрихкод</label>
                <input style={styles.input} placeholder="Необов'язково" value={form.barcode}
                  onChange={e => setForm(f => ({...f, barcode:e.target.value}))} />
              </div>
            </div>
            <label style={styles.checkbox}>
              <input type="checkbox" checked={form.is_excise}
                onChange={e => setForm(f => ({...f, is_excise:e.target.checked}))} />
              🏷 Акцизний (пиво/сидр/вино)
            </label>
            <div style={styles.row}>
              <div style={styles.fg}>
                <label style={styles.label}>Код УКТ ЗЕД</label>
                <input style={styles.input} placeholder="10 цифр, напр. 2203001000" value={form.uktzed}
                  onChange={e => setForm(f => ({...f, uktzed:e.target.value}))} />
                <span style={styles.hint}>Беріть з накладної постачальника. Пиво розлив 2203001000, сидр 2206003100.</span>
              </div>
              <div style={{...styles.fg, maxWidth:'200px'}}>
                <label style={styles.label}>Податкова група</label>
                <input style={styles.input} placeholder={form.is_excise ? 'Д (акциз 5%)' : 'порожньо'} value={form.tax_group}
                  onChange={e => setForm(f => ({...f, tax_group:e.target.value}))} />
                <span style={styles.hint}>Алкоголь на єдиному податку → «Д».</span>
              </div>
            </div>
            {error && <p style={styles.error}>{error}</p>}
            <div style={styles.formBtns}>
              <button type="submit" style={styles.addBtn}>{editingId ? 'Зберегти' : 'Додати'}</button>
              <button type="button" style={styles.ghostBtn}
                onClick={() => { setShowForm(false); setForm(emptyForm); setEditingId(null); }}>Скасувати</button>
            </div>
          </form>
        </div>
      )}

      <div style={styles.filters}>
        <input style={styles.search} placeholder="🔍 Пошук товару..."
          value={search} onChange={e => setSearch(e.target.value)} />
        <select style={styles.catSelect} value={filterCat} onChange={e => setFilterCat(e.target.value)}>
          <option value="all">Всі категорії</option>
          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      {loading ? <p style={styles.muted}>Завантаження...</p> : (
        <div style={styles.card}>
          <div style={{...styles.trow, ...styles.thead}}>
            <span style={styles.cName}>Товар</span>
            <span style={styles.cCat}>Категорія</span>
            <span style={styles.cPrice}>Ціна</span>
            <span style={styles.cUnit}>Од.</span>
            <span style={styles.cCode}>УКТ ЗЕД</span>
            <span style={styles.cTax}>Податок</span>
            <span style={styles.cAct}></span>
          </div>
          {filtered.length === 0 && <p style={styles.muted}>Нічого не знайдено</p>}
          {filtered.map(p => (
            <div key={p.id} style={styles.trow}>
              <span style={styles.cName}>
                {p.name}
                {p.is_excise && <span style={styles.tag}>🏷</span>}
                {p.is_draft && <span style={styles.tag}>🍺</span>}
              </span>
              <span style={styles.cCat}>{catName(p.category_id)}</span>
              <span style={styles.cPrice}>{p.price} ₴</span>
              <span style={styles.cUnit}>{UNITS[p.unit] || p.unit}</span>
              <span style={styles.cCode}>{p.uktzed || <span style={styles.miss}>—</span>}</span>
              <span style={styles.cTax}>{p.tax_group || '—'}</span>
              <span style={styles.cAct}>
                {isSuperAdmin && <>
                  <button style={styles.editBtn} onClick={() => startEdit(p)}>✏️</button>
                  <button style={styles.delBtn} onClick={() => removeProduct(p)}>🗑</button>
                </>}
              </span>
            </div>
          ))}
        </div>
      )}
    </AppShell>
  );
}

const styles = {
  head: { display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:'16px', marginBottom:'18px', flexWrap:'wrap' },
  h1: { margin:'0 0 4px', fontSize:'22px', color:'#f4ead7' },
  sub: { margin:0, color:'#9a8a6e', fontSize:'13px', maxWidth:'640px' },
  headBtns: { display:'flex', gap:'10px' },
  addBtn: { padding:'9px 18px', background:'#f5a623', color:'#14110c', border:'none', borderRadius:'8px', fontWeight:'bold', cursor:'pointer', fontSize:'14px' },
  ghostBtn: { padding:'9px 16px', background:'transparent', color:'#f4ead7', border:'1px solid #38301f', borderRadius:'8px', cursor:'pointer', fontSize:'14px' },
  card: { background:'#211c14', border:'1px solid #38301f', borderRadius:'12px', padding:'16px', marginBottom:'16px' },
  row: { display:'flex', gap:'12px', flexWrap:'wrap' },
  fg: { flex:1, minWidth:'160px', marginBottom:'12px' },
  label: { display:'block', color:'#9a8a6e', fontSize:'11px', marginBottom:'5px', textTransform:'uppercase', letterSpacing:'0.5px' },
  input: { width:'100%', padding:'10px 14px', borderRadius:'8px', border:'1px solid #38301f', background:'#1c1812', color:'#fff', fontSize:'14px', boxSizing:'border-box' },
  hint: { display:'block', color:'#6b5d44', fontSize:'11px', marginTop:'4px', lineHeight:1.4 },
  checkbox: { display:'flex', alignItems:'center', gap:'8px', cursor:'pointer', fontSize:'14px', color:'#9a8a6e', marginBottom:'12px' },
  error: { color:'#ff6b6b', fontSize:'13px', marginBottom:'10px' },
  formBtns: { display:'flex', gap:'10px' },
  filters: { display:'flex', gap:'10px', marginBottom:'14px', flexWrap:'wrap' },
  search: { flex:1, minWidth:'200px', padding:'10px 14px', borderRadius:'8px', border:'1px solid #38301f', background:'#211c14', color:'#fff', fontSize:'14px', boxSizing:'border-box' },
  catSelect: { padding:'10px 14px', borderRadius:'8px', border:'1px solid #38301f', background:'#211c14', color:'#fff', fontSize:'14px' },
  trow: { display:'flex', alignItems:'center', gap:'10px', padding:'10px 8px', borderBottom:'1px solid #2a2419', fontSize:'14px' },
  thead: { color:'#9a8a6e', fontSize:'11px', textTransform:'uppercase', letterSpacing:'0.5px' },
  cName: { flex:1, minWidth:'180px', display:'flex', alignItems:'center', gap:'6px' },
  cCat: { width:'140px', color:'#c9b896' },
  cPrice: { width:'80px', textAlign:'right' },
  cUnit: { width:'40px', color:'#9a8a6e' },
  cCode: { width:'110px', fontFamily:'ui-monospace,Menlo,monospace', color:'#c9b896' },
  cTax: { width:'70px', textAlign:'center', color:'#c9b896' },
  cAct: { width:'80px', display:'flex', gap:'6px', justifyContent:'flex-end' },
  tag: { fontSize:'12px' },
  miss: { color:'#ff6b6b' },
  editBtn: { background:'#38301f', border:'none', borderRadius:'6px', padding:'5px 8px', cursor:'pointer', fontSize:'13px' },
  delBtn: { background:'rgba(255,107,107,.18)', border:'none', borderRadius:'6px', padding:'5px 8px', cursor:'pointer', fontSize:'13px' },
  muted: { color:'#9a8a6e', textAlign:'center', padding:'16px' },
};
